/**
 * GRN Dashboard Header Component
 * Enterprise-grade metrics summary bar for GRN page
 * 
 * Phase 2 of GRN Enterprise Upgrade
 * 
 * @component
 */
import React, { useState, useEffect } from 'react';
import {
  Package,
  Clock,
  CheckCircle,
  AlertTriangle,
  AlertCircle,
  TrendingUp,
  Calendar,
  RefreshCw,
  Loader2,
} from 'lucide-react';
import { Card, CardContent } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../ui/tooltip';
import api from '../../../services/api';

const MetricCard = ({ 
  icon: Icon, 
  label, 
  value, 
  subValue, 
  color = 'text-gray-600',
  bgColor = 'bg-gray-50',
  trend,
  tooltip
}) => {
  const content = (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-lg ${bgColor} border`}>
      <div className={`p-2 rounded-lg ${bgColor} ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-500 truncate">{label}</p>
        <div className="flex items-baseline gap-2">
          <p className={`text-xl font-bold ${color}`}>{value}</p>
          {subValue && (
            <span className="text-xs text-gray-400">{subValue}</span>
          )}
          {trend && (
            <span className={`text-xs ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
              {trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%
            </span>
          )}
        </div>
      </div>
    </div>
  );

  if (tooltip) {
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent side="bottom">
            <p className="text-sm">{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return content;
};

const AgingBar = ({ breakdown }) => {
  const total = breakdown.reduce((sum, item) => sum + item.count, 0);
  if (total === 0) return null;

  const getColor = (status) => {
    switch (status) {
      case 'OVERDUE': return 'bg-red-500';
      case 'WARNING': return 'bg-amber-500';
      default: return 'bg-green-500';
    }
  };

  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden flex">
        {breakdown.map((item, idx) => {
          const width = (item.count / total) * 100;
          if (width === 0) return null;
          return (
            <TooltipProvider key={idx}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div 
                    className={`h-full ${getColor(item.status)} transition-all`}
                    style={{ width: `${width}%` }}
                  />
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-sm">{item.label}: {item.count} GRNs</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          );
        })}
      </div>
      <span className="text-xs text-gray-500 w-12">{total} open</span>
    </div>
  );
};

export const GRNDashboardHeader = ({ onRefresh }) => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchDashboard = async (showRefresh = false) => {
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    
    try {
      const response = await api.get('/api/v1/inventory/v2/grn/dashboard/summary');
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching GRN dashboard:', err);
      setError('Failed to load metrics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboard();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(() => fetchDashboard(true), 60000);
    return () => clearInterval(interval);
  }, []);

  const handleRefresh = () => {
    fetchDashboard(true);
    onRefresh?.();
  };

  if (loading) {
    return (
      <Card className="mb-4">
        <CardContent className="p-4">
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-gray-500">Loading metrics...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card className="mb-4 border-red-200">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-red-600 text-sm">{error || 'No data available'}</span>
            <Button variant="ghost" size="sm" onClick={() => fetchDashboard()}>
              <RefreshCw className="w-4 h-4 mr-1" /> Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  const pendingCount = (data.by_status?.DRAFT || 0) + 
                       (data.by_status?.RECEIVED || 0) + 
                       (data.by_status?.INSPECTING || 0);

  return (
    <Card className="mb-4 border-blue-100 bg-gradient-to-r from-blue-50/50 to-white" data-testid="grn-dashboard-header">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Package className="w-5 h-5 text-blue-600" />
            <h3 className="font-semibold text-gray-800">GRN Overview</h3>
            {data.aging?.overdue > 0 && (
              <Badge variant="destructive" className="text-xs">
                {data.aging.overdue} Overdue
              </Badge>
            )}
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
            className="text-gray-500 hover:text-gray-700"
          >
            <RefreshCw className={`w-4 h-4 mr-1 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Main Metrics Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-3">
          <MetricCard
            icon={Package}
            label="Total GRNs"
            value={data.total}
            color="text-blue-600"
            bgColor="bg-blue-50"
            tooltip="All GRNs in the system"
          />
          
          <MetricCard
            icon={Clock}
            label="Pending"
            value={pendingCount}
            subValue={`${data.by_status?.DRAFT || 0} draft`}
            color="text-amber-600"
            bgColor="bg-amber-50"
            tooltip="GRNs in Draft, Received, or Inspecting status"
          />
          
          <MetricCard
            icon={CheckCircle}
            label="Completed"
            value={data.by_status?.COMPLETED || 0}
            color="text-green-600"
            bgColor="bg-green-50"
            tooltip="Successfully processed GRNs"
          />
          
          <MetricCard
            icon={Calendar}
            label="Today"
            value={data.today?.created || 0}
            subValue={`${data.today?.completed || 0} done`}
            color="text-purple-600"
            bgColor="bg-purple-50"
            tooltip={`Created today: ${data.today?.created}, Completed today: ${data.today?.completed}`}
          />
          
          <MetricCard
            icon={AlertCircle}
            label="Overdue (>48h)"
            value={data.aging?.overdue || 0}
            color={data.aging?.overdue > 0 ? "text-red-600" : "text-gray-500"}
            bgColor={data.aging?.overdue > 0 ? "bg-red-50" : "bg-gray-50"}
            tooltip="GRNs exceeding 48-hour SLA"
          />
          
          <MetricCard
            icon={TrendingUp}
            label="Avg Processing"
            value={`${data.performance?.avg_processing_hours || 0}h`}
            color="text-teal-600"
            bgColor="bg-teal-50"
            tooltip="Average time from creation to completion"
          />
        </div>

        {/* Aging Progress Bar */}
        {data.aging?.breakdown && (
          <div className="pt-2 border-t border-gray-100">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-500">Aging Distribution (Open GRNs)</span>
              <div className="flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                  &lt;24h ({data.aging?.on_time || 0})
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  24-48h ({data.aging?.warning || 0})
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                  &gt;48h ({data.aging?.overdue || 0})
                </span>
              </div>
            </div>
            <AgingBar breakdown={data.aging.breakdown} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GRNDashboardHeader;
